import { inject, Injectable, Injector, NgZone } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { App } from '@capacitor/app';
import { StatusBar } from '@capacitor/status-bar';
import { Platform } from '@ionic/angular/standalone';
import { NotificationClickEvent } from 'onesignal-cordova-plugin';
import { firstValueFrom } from 'rxjs';
import { filter, first, switchMap } from 'rxjs/operators';
import { authLog } from 'src/app/auth/auth.handler';
import { IAppUpdateCheckDto, IMaharahAppConfigsDto } from 'src/app/contracts';
import { NotificationType, UserSegmentationTag } from 'src/app/contracts/api.types';
import { AppSettings } from 'src/app/core/common/app.settings';
import { Auth } from 'src/app/core/common/auth';
import { Back } from 'src/app/core/common/back';
import { Configs } from 'src/app/core/common/configs';
import { ErrorTracker } from 'src/app/core/common/error.tracker';
import { Push } from 'src/app/core/common/push';
import { backButton$, IPushNotificationDetails, Logs, Startup } from 'src/app/core/contracts';
import { log, Logger } from 'src/app/core/helpers/logger';
import { setStorageFailureReporter } from 'src/app/core/helpers/storage';
import { UpdateHandler } from 'src/app/core/helpers/update.handler';
import { delay, isCapacitor, isIOS, TIMED_OUT, withTimeout } from 'src/app/core/helpers/utils';
import { EvaluationData } from 'src/app/data/evaluation.data';
import { UserTagsData } from 'src/app/data/user-tags.data';
import { AdjustService } from 'src/app/services/adjust.service';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { GenesysService } from 'src/app/services/genesys.service';
import { MarketingService } from 'src/app/services/marketing.service';
import { SocialMediaService } from 'src/app/services/social-media.service';
import { TrackingConsentService } from 'src/app/services/tracking-consent.service';

@Injectable()
export class Initializer {
  private platform = inject(Platform);
  private settings = inject(AppSettings);
  private push = inject(Push);
  private injector = inject(Injector);
  private analytics = inject(AnalyticsService);
  private adjust = inject(AdjustService);
  private genesys = inject(GenesysService);
  private socialMedia = inject(SocialMediaService);
  private trackingConsent = inject(TrackingConsentService);
  private logger = inject(Logger);
  marketing = inject(MarketingService);
  private userTags = inject(UserTagsData);

  private lastBackgroundedAt: number | null = null;
  private readonly sessionGapMs = 30 * 1000; // OneSignal counts a new session after >30s away — mirror it
  /** Steps still running — what the deadline report names as the likely culprit. */
  private pendingSteps: string[] = [];
  private finalized = false;

  /** APP_INITIALIZER entry point — resolves within Startup.INIT_TIMEOUT_MS and never rejects. */
  public async init(): Promise<void> {
    const startedAt = Date.now();
    setStorageFailureReporter(failure => this.logger.error(failure)); // storage is DI-free; anything it recorded before this is reported now
    const startup = this.runStartup();
    const outcome = await withTimeout(startup, Startup.INIT_TIMEOUT_MS);
    if (outcome === TIMED_OUT) {
      const pending = [...this.pendingSteps];
      this.logger.error({
        msg: '@initializer: startup did not finish within the deadline, continuing in degraded mode',
        deadlineMs: Startup.INIT_TIMEOUT_MS,
        pending,
      });
      this.finalize();
      this.reportLateSettlement(startup, startedAt, pending); // not awaited — the chain keeps running in the background
    }
    authLog(`auth.flow::=>initializer.init: completed`, { elapsedMs: Date.now() - startedAt });
  }
  /** Runs the whole chain and guarantees it settles without rejecting — a rejected initializer fails bootstrap, which is
   *  a green screen. */
  private async runStartup(): Promise<void> {
    try {
      await this.startup();
    } catch (err) {
      this.logger.error({ msg: '@initializer: startup failed unexpectedly, continuing in degraded mode', pending: this.pendingSteps, err });
      this.finalize();
    }
  }
  /** Tells "slow" from "hung" in Sentry once the deadline has passed. Logger.error on purpose — only error-level reaches
   *  Sentry; elapsedMs stays out of the message so the fingerprint stays stable. */
  private async reportLateSettlement(startup: Promise<void>, startedAt: number, pending: string[]) {
    await startup;
    this.logger.error({ msg: '@initializer: startup finished after the deadline', elapsedMs: Date.now() - startedAt, pending });
  }
  private async startup(): Promise<void> {
    const auth = this.injector.get(Auth);
    try {
      // pre-auth setup, caught as a group: a failure here must never skip auth.checkAuth() — without it isReady$ never
      // turns true and the route guards block the first page forever
      this.pendingSteps = ['platform.ready'];
      await this.platform.ready();
      if (isCapacitor && !isIOS) {
        this.pendingSteps = ['statusBar.overlay'];
        await StatusBar.setOverlaysWebView({ overlay: false });
      }
      this.pendingSteps = ['setup'];
      this.initTracking(); // deliberately not awaited — bootstrap must not block on the permission prompts (see initTracking)
      this.genesys.init();
      const router = this.injector.get(Router);
      this.handleBackButton(router);
      this.setupNotifications(router, auth);
      this.setupSessionTracking();
    } catch (err) {
      this.logger.error({ msg: '@initializer: pre-auth setup failed, continuing', pending: this.pendingSteps, err });
    }

    this.pendingSteps = ['auth.checkAuth', 'settings.initLanguage'];
    await Promise.all([
      auth.checkAuth().then(() => {
        this.pendingSteps = this.pendingSteps.filter(step => step !== 'auth.checkAuth');
        authLog(`auth.flow::=>initializer.auth.checkAuth: completed`);
      }),
      this.settings.initLanguage().then(() => {
        this.pendingSteps = this.pendingSteps.filter(step => step !== 'settings.initLanguage');
        authLog(`auth.flow::=>initializer.settings.initLanguage: completed`);
      }),
    ]);
    authLog(`auth.flow::=>initializer: completed auth & settings initialization`);

    this.pendingSteps = ['configs.load'];
    const configs = this.injector.get(Configs);
    // { defaultValue }: Api.request() returns EMPTY while offline, so loadConfigs() can complete without emitting — a bare
    // firstValueFrom() rejects with EmptyError there, i.e. a deterministic green screen on an airplane-mode launch
    const loadedConfigs = await firstValueFrom(configs.loadConfigs(), { defaultValue: {} as IMaharahAppConfigsDto });
    this.pendingSteps = [];
    this.finalize(loadedConfigs?.updateCheck);
  }
  /** Post-startup work that must happen exactly once before the first page renders — after the chain completes, or at
   *  the deadline with whatever is available by then (a still-running chain then skips it). */
  private finalize(updateCheck?: IAppUpdateCheckDto) {
    if (this.finalized) {
      return;
    }
    this.finalized = true;
    this.settings.ensureInitialized(); // BaseComponent.settings must never be undefined — SplashPage reads it right after hiding the native splash
    this.injector.get(UpdateHandler).checkForUpdates(updateCheck);
    this.injector.get(ErrorTracker).init();
  }
  /**
   * Startup prompt + tracking choreography. Firebase/Clarity are first-party analytics (not
   * ATT-scoped) and start immediately. On iOS, the tracking SDKs (Adjust, Facebook, ad pixels)
   * must wait for the App Tracking Transparency answer (App Store Guideline 5.1.2) so their first
   * session carries the user's choice. The push permission prompt is requested only after the ATT
   * one is answered so the two system alerts never race, and OneSignal in-app messages (paused in
   * push.init()) resume only after both prompts plus a settle delay — deterministic order:
   * ATT alert → push alert → marketing IAMs. Deliberately not awaited by startup() — app bootstrap
   * must not block on the prompts — so it never rejects: its failures are logged here.
   */
  private async initTracking() {
    try {
      this.analytics.init();
      const trackingAllowed = await this.trackingConsent.requestPermission();
      this.adjust.init();
      this.socialMedia.init(trackingAllowed);
      await this.push.requestPermission();
      await delay(Startup.IAM_RESUME_DELAY_MS);
      this.push.resumeInAppMessages();
    } catch (err) {
      this.logger.error({ msg: '@initializer: tracking / permissions flow failed', err });
    }
  }
  private handleBackButton(router: Router) {
    const back = this.injector.get(Back);
    this.platform.backButton.subscribeWithPriority(5, async processNextHandler => {
      if (backButton$.observed) {
        log('@handleBackButton: forward to backButton$');
        backButton$.next();
      } else {
        const { exit, canGoBack, redirectTo } = await back.canBackWithPhysicalButton(router.url);
        if (Logs.ENABLE_BACK_NAVIGATION_LOGS) {
          log(`@canGoBack result: canGoBack: ${canGoBack}, exit: ${exit}, redirectTo: ${redirectTo?.toString()}`);
        }

        if (exit) {
          App.exitApp();
          return;
        }
        if (redirectTo) {
          router.navigate([redirectTo]);
          return;
        }
        if (canGoBack) {
          processNextHandler();
        }
      }
    });
  }
  private setupNotifications(router: Router, auth: Auth) {
    const onNotificationOpened = (ev: NotificationClickEvent) => {
      const details = ev.notification.additionalData as IPushNotificationDetails;
      log(`@push.opened: type=${details?.type}, data=${JSON.stringify(details)}`);
      this.userTags.track(UserSegmentationTag.PushOpen); // #6 — every push open, regardless of type / deep-link
      if (!details) {
        return;
      }
      const zone = this.injector.get(NgZone);
      let action: () => void;
      switch (details.type) {
        case NotificationType.ResourceArrived:
        case NotificationType.ResourceOnTheWay:
        case NotificationType.VisitStarted:
        case NotificationType.VisitCompleted:
          action = () => {
            zone.run(() => {
              // run inside angular zone otherwise hourly-contracts/details will be blank
              router.navigate([`hourly/details/${details.bookingId}`]);
            });
          };
          break;
        case NotificationType.MonthlyContractRenewReminder:
          action = () => {
            zone.run(() => {
              router.navigate([`monthly/details/${details.requestId}`]);
            });
          };
          break;
        case NotificationType.EvaluateHourlyVisitEnded:
        case NotificationType.EvaluateHourlyContractEnded:
        case NotificationType.EvaluateHourlyContractVisitEnded:
        case NotificationType.EvaluateMonthlyContractEnded:
        case NotificationType.EvaluateMonthlyContractFreezed:
          action = () => {
            zone.run(() => {
              const evaluationData = this.injector.get(EvaluationData);
              evaluationData.evaluationOpenedFromNotification = true;
              log(`@push.evaluation: userEvaluationId=${details.userEvaluationId}`);
              evaluationData.openEvaluation(evaluationData.getUserEvaluation(details.userEvaluationId as number)).subscribe();
            });
          };
          break;
        default:
          action = () => {};
      }
      if (router.url === '/') {
        // the app was closed
        router.events
          .pipe(
            filter(evt => evt instanceof NavigationEnd),
            first(),
            switchMap(() =>
              auth.isAuthenticated$.pipe(
                filter(isAuthenticated => isAuthenticated),
                first()
              )
            )
          )
          .subscribe(action);
      } else {
        // the app is in the background/foreground
        // if router.url === '/location'  location.back()
        action();
      }
    };
    this.push.init(onNotificationOpened);
  }
  private setupSessionTracking() {
    // #10 activity-drop signal. A "session" = app open / foreground (session.md decision). Emit on cold launch and
    // on foreground-resume only when backgrounded > 30s, mirroring OneSignal's own session rule. The data service
    // no-ops when unauthenticated. Feeds sig.UserTaggingEvents(session_start) → the daily activity_dropped TVF.
    this.userTags.track(UserSegmentationTag.SessionStart);
    App.addListener('appStateChange', ({ isActive }) => {
      if (!isActive) {
        this.lastBackgroundedAt = Date.now();
      } else if (this.lastBackgroundedAt && Date.now() - this.lastBackgroundedAt > this.sessionGapMs) {
        this.userTags.track(UserSegmentationTag.SessionStart);
      }
    });
  }
}
